<template>
  <EChart
    :key="key"
    ref="echartRef"
    :option
    :selected-index
    @click:item="handleClickItem"
  />
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { sumBy } from 'lodash'

import type { DefaultLabelFormatterCallbackParams, EChartOption } from 'echarts'

import { CHART_OTHER_TITLE } from './utils/const'
import { BLUR_ASSET_NAMES, BLUR_DIGITAL_VALUES } from '@/const/storage'

import EChart from './EChart.vue'

type Props = {
  data: EChartOption.SeriesPie.DataObject[]
  title?: string

  maxSlices?: number
  total?: number

  colors?: string[]

  valueFormatter?: (data: number) => string
  percentFormatter?: (data: number, withSign?: boolean) => string
}

type Emits = {
  'click:slice': [index: number, name: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const echartRef = useTemplateRef('echartRef')

defineExpose({
  getInstance() {
    return echartRef.value?.getInstance()
  },
})

const outerSelectedIndex = defineModel<number>('index')
const page = defineModel<number>('page', { default: 0 })

const blurAssetNames = useLocalStorage(BLUR_ASSET_NAMES, false)
const blurDigitalValues = useLocalStorage(BLUR_DIGITAL_VALUES, false)

const key = computed(() => {
  const result = []
  if (blurAssetNames.value) result.push('blur-asset-names')
  if (blurDigitalValues.value) result.push('blur-digital-values')
  return result.join('-')
})

const offset = computed(() => page.value * (props.maxSlices || 0))

const otherTotalValue = computed(() => {
  return (
    props.maxSlices &&
    sumBy(props.data.slice(offset.value + props.maxSlices), 'value')
  )
})

const selectedIndex = computed(() =>
  outerSelectedIndex.value !== undefined
    ? outerSelectedIndex.value - offset.value
    : undefined,
)

const data = computed(() => {
  if (!props.maxSlices) return props.data
  const leavingsLength = props.data.length - offset.value
  const length = Math.min(props.maxSlices, leavingsLength)
  return props.data.slice(offset.value, offset.value + length)
})

const seriesPie = computed<EChartOption.SeriesPie>(() => {
  const result = data.value.map((item, index) => {
    const value = Math.abs(item.value || 0)
    const isNegative = item.value && item.value < 0
    return {
      name: item.name,
      value,
      isNegative,
      ...(props.colors
        ? {
          itemStyle: {
            borderColor: props.colors[index],
            color: props.colors[index],
          },
        }
        : {}),
    }
  })

  const leavingsLength = props.data.length - offset.value
  if (props.maxSlices && leavingsLength > props.maxSlices) {
    const isNegative = result.at(-1)?.isNegative
    result.push({
      name: CHART_OTHER_TITLE,
      value: Math.abs(otherTotalValue.value || 0),
      isNegative,
      ...(props.colors
        ? {
          itemStyle: {
            borderColor: `${props.colors.at(-1)}`,
            color: `${props.colors.at(-1)}`,
          },
        }
        : {}),
    })
  }

  return {
    type: 'pie',
    selectedMode: 'single',
    radius: '70%',
    label: {
      formatter: (params: DefaultLabelFormatterCallbackParams) => {
        const { name, percent, value } = params
        const displayName = blurAssetNames.value ? '****' : name
        const displayValue = blurDigitalValues.value ? '???' : calculatePercent(Number(value), Number(percent))
        return `{name|${displayName}}\n{value|${displayValue}%}`
      },
    },
    top: props.title || props.total ? '3%' : '0%',
    left: '0%',
    right: '0%',
    bottom: '0%',
    data: result,
  }
})

const option = computed(() => ({
  ...(props.title
    ? {
      title: {
        text: props.title,
        left: 'center',
        textStyle: {
          fontSize: 12,
          fontWeight: 500,
          color: '#BCBCBC',
        } as EChartOption.TextStyleWithRich,
      },
    }
    : {}),
  tooltip: {
    formatter: (
      params: EChartOption.Tooltip.Format | EChartOption.Tooltip.Format[]
    ) => {
      const { color, data, name, value } = params as EChartOption.Tooltip.Format
      const sign = data.isNegative ? '-' : ''
      const result = props.valueFormatter?.(Number(value)) || value
      return `<div style="color:${color}">${name}</div><span class="number-node">${sign}${result}</span>`
    },
    confine: true,
    extraCssText: 'max-width: 50vw; word-wrap: break-word; white-space: normal;',
  },
  series: [seriesPie.value],
}))

const calculatePercent = (value: number, percent: number) => {
  const result = props.total ? Math.abs(value / props.total) * 100 : percent
  return props.percentFormatter?.(result, false) || result
}

const handleClickItem = (index: number, name: string) => {
  if (name === CHART_OTHER_TITLE) {
    page.value++
    outerSelectedIndex.value = undefined
  } else if (index === selectedIndex.value) {
    outerSelectedIndex.value = undefined
  } else {
    outerSelectedIndex.value = offset.value + index
  }
  emit('click:slice', index, name)
}

watch(
  outerSelectedIndex,
  value => {
    if (value === undefined || !props.maxSlices) return
    const nextPage = (value - (value % props.maxSlices)) / props.maxSlices
    page.value = nextPage
  },
  { immediate: true },
)
</script>
